/**
 * EASY_PAINTING_LandingPage_v01A_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01A
 * Date: 2025-08-29 (America/Denver)
 *
 * Highlights:
 * - Same UX patterns as renters v02O (wizard/vertical, progress, safe submit).
 * - Sections are reusable components used by BOTH wizard and vertical views.
 * - Clean, sectioned confirmation summary (no JSON).
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

import { FAQ_Component_v02b_Simple } from "../../component-library/FAQ_Component_v02b-simple";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01A";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "services/images/PAINTING/";

/* ============================================================
   TYPES
============================================================ */
type PropertyType =
  | ""
  | "single_family"
  | "apartment"
  | "condo"
  | "townhome"
  | "duplex"
  | "commercial"
  | "other";

type ProjectType = "" | "interior" | "exterior" | "both";
type Stories = "" | "1" | "2" | "3+";
type Flex = "" | "asap" | "within_2_weeks" | "flexible";
type Occupancy = "" | "occupied" | "vacant" | "between_tenants";

/** Raw lead payload (typed for app state) */
interface ILeadPayload {
  // Contact
  fullName: string;
  email: string;
  phone: string;

  // Job Location
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  // Property
  propertyType: PropertyType;
  stories?: Stories;
  approxSqFt?: string;
  roomsCount?: string;

  // Scope
  projectType: ProjectType;
  paintProvided?: boolean;
  colorSelected?: boolean;

  // Surfaces (we'll store as string[] for clarity)
  surfaces?: string[];

  // Prep & Access
  needsRepairs?: boolean;
  wallpaperRemoval?: boolean;
  popcornRemoval?: boolean;
  pressureWash?: boolean;
  occupancy?: Occupancy;
  accessNotes?: string;

  // Timeline & Budget
  preferredStartDate?: string;
  timingFlexibility?: Flex;
  budgetMin?: string;
  budgetMax?: string;

  // Notes / Photos
  notes?: string;
  photoUrls?: string; // newline or comma-separated list of URLs

  // Meta
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string {
  return (s ?? "").replace(/\D+/g, "");
}
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function isValidPhone(raw: string): boolean {
  return digitsOnly(raw).length === 10;
}
function isValidZipCode(raw: string): boolean {
  return digitsOnly(raw).length === 5;
}
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw || "—";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
const money = (nLike?: string) => {
  if (!nLike) return "—";
  const n = Number(digitsOnly(nLike));
  if (!Number.isFinite(n)) return nLike;
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  } catch {
    return `$${n}`;
  }
};
const dash = (v?: string) => (v && v.trim().length ? v : "—");
const cap = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_425991011_Preview.jpeg",
    alt: "Painter rolling fresh paint on interior wall.",
    title: "Freshen Up Your Space",
    subline: "Interior & exterior pros for homes and businesses.",
    smallCaption: "Clean lines. Solid prep.",
  },
  {
    src: imagePath + "AdobeStock_318845201_Preview.jpeg",
    alt: "Exterior house painting on a sunny day.",
    title: "Exterior Done Right",
    subline: "From prep to finish, we handle every detail.",
    smallCaption: "Durable finishes.",
  },
  {
    src: imagePath + "AdobeStock_208640585_Preview.jpeg",
    alt: "Painter spraying siding with protective mask.",
    title: "Pro Prep & Protection",
    subline: "Repair, prime, and paint for lasting results.",
    smallCaption: "Licensed & insured.",
  },
];

/* ============================================================
   FAQs (simple)
============================================================ */
// const paintingFAQs = [
//   { q: "Do you help with color selection?", a: "Yes—your pro can recommend palettes and finish types." },
//   { q: "Is paint included?", a: "Either way: we can supply paint or use your preferred brand." },
//   { q: "How soon can you start?", a: "It depends on scope and seasonality, but we’ll reach out quickly." },
// ];

/* ============================================================
   MAIN
============================================================ */
export const EASY_PAINTING_LandingPage_v02O_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Location
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Property
  const [propertyType, setPropertyType] = useState<PropertyType>("");
  const [stories, setStories] = useState<Stories>("");
  const [approxSqFt, setApproxSqFt] = useState<string>("");
  const [roomsCount, setRoomsCount] = useState<string>("");

  // Scope
  const [projectType, setProjectType] = useState<ProjectType>("");
  const [paintProvided, setPaintProvided] = useState<boolean>(false);
  const [colorSelected, setColorSelected] = useState<boolean>(false);

  // Surfaces (checkboxes -> collected into array for payload)
  const [surfWalls, setSurfWalls] = useState(true);
  const [surfCeilings, setSurfCeilings] = useState(false);
  const [surfTrim, setSurfTrim] = useState(false);
  const [surfDoors, setSurfDoors] = useState(false);
  const [surfCabinets, setSurfCabinets] = useState(false);
  const [surfDeckFence, setSurfDeckFence] = useState(false);
  const [surfSiding, setSurfSiding] = useState(false);
  const [surfStucco, setSurfStucco] = useState(false);
  const [surfBrick, setSurfBrick] = useState(false);

  // Prep & Access
  const [needsRepairs, setNeedsRepairs] = useState(false);
  const [wallpaperRemoval, setWallpaperRemoval] = useState(false);
  const [popcornRemoval, setPopcornRemoval] = useState(false);
  const [pressureWash, setPressureWash] = useState(false);
  const [occupancy, setOccupancy] = useState<Occupancy>("");
  const [accessNotes, setAccessNotes] = useState("");

  // Timeline & Budget
  const [preferredStartDate, setPreferredStartDate] = useState("");
  const [timingFlexibility, setTimingFlexibility] = useState<Flex>("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");

  // Notes / Photos
  const [notes, setNotes] = useState("");
  const [photoUrls, setPhotoUrls] = useState("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Location", "Scope & Surfaces", "Prep, Timeline & Notes", "Confirmation"] as const;

  /* ---------- Touched + validation helpers ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZipCode(zipCode),

    propertyType: propertyType !== "",
    projectType: projectType !== "",

    approxSqFt: !approxSqFt || /^\d{2,6}$/.test(digitsOnly(approxSqFt)),
    roomsCount: !roomsCount || /^\d{1,2}$/.test(digitsOnly(roomsCount)),
    budgetMin: !budgetMin || /^\d{2,6}$/.test(digitsOnly(budgetMin)),
    budgetMax: !budgetMax || /^\d{2,6}$/.test(digitsOnly(budgetMax)),

    accessNotes: accessNotes.trim().length === 0 || accessNotes.trim().length >= 2,
    notes: notes.trim().length === 0 || notes.trim().length >= 2,
    photoUrls: photoUrls.trim().length === 0 || photoUrls.trim().length >= 5,
  };

  const valid = useMemo(() => {
    return (
      V.fullName && V.email && V.phone &&
      V.address1 && V.city && V.state && V.zip &&
      V.propertyType && V.projectType &&
      V.approxSqFt && V.roomsCount && V.budgetMin && V.budgetMax &&
      V.accessNotes && V.notes && V.photoUrls
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.address1 && V.city && V.state && V.zip && V.propertyType;
      case 1:
        return V.projectType && V.approxSqFt && V.roomsCount; // surfaces are optional
      case 2:
        return V.budgetMin && V.budgetMax && V.accessNotes && V.notes && V.photoUrls;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName", "email", "phone", "address1", "city", "state", "zip", "propertyType"]);
    else if (currentStep === 1) touchMany(["projectType", "approxSqFt", "roomsCount"]);
    else if (currentStep === 2) touchMany(["budgetMin", "budgetMax", "accessNotes", "notes", "photoUrls"]);

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT
  ============================================================ */
  function buildLeadPayload(): ILeadPayload {
    const surfaces: string[] = [];
    if (surfWalls) surfaces.push("walls");
    if (surfCeilings) surfaces.push("ceilings");
    if (surfTrim) surfaces.push("trim");
    if (surfDoors) surfaces.push("doors");
    if (surfCabinets) surfaces.push("cabinets");
    if (surfDeckFence) surfaces.push("deck_fence");
    if (surfSiding) surfaces.push("siding");
    if (surfStucco) surfaces.push("stucco");
    if (surfBrick) surfaces.push("brick");

    return {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      propertyType,
      stories,
      approxSqFt: approxSqFt ? String(Number(digitsOnly(approxSqFt))) : "",
      roomsCount: roomsCount ? String(Number(digitsOnly(roomsCount))) : "",

      projectType,
      paintProvided,
      colorSelected,

      surfaces,

      needsRepairs,
      wallpaperRemoval,
      popcornRemoval,
      pressureWash,
      occupancy,
      accessNotes: accessNotes?.trim() || "",

      preferredStartDate,
      timingFlexibility,
      budgetMin: budgetMin ? String(Number(digitsOnly(budgetMin))) : "",
      budgetMax: budgetMax ? String(Number(digitsOnly(budgetMax))) : "",

      notes: notes?.trim() || "",
      photoUrls: photoUrls?.trim() || "",

      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.painting.quote",
      itemClass: "quote.request",
      itemType: "services.painting.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (typeof v === "number") out[k] = Number.isFinite(v) ? `${v}` : "";
      else if (typeof v === "boolean") out[k] = v ? "true" : "false";
      else if (v && typeof v === "object") {
        try {
          out[k] = JSON.stringify(v);
        } catch {
          out[k] = "";
        }
      } else out[k] = "";
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }
    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      address1: true, city: true, state: true, zip: true, propertyType: true,
      projectType: true, approxSqFt: true, roomsCount: true,
      budgetMin: true, budgetMax: true, accessNotes: true, notes: true, photoUrls: true,
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);

      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmittedApp(appPayload);
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Clamp active slide
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Easy <span className="text-primary">Painting</span> Quotes
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with a project advisor:</div>
              <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Interior & Exterior</Badge>
                  <Badge bg="light" text="dark">Repair & Prep</Badge>
                  <Badge bg="light" text="dark">Licensed & Insured</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" href="#faq">Common Questions</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast, No-Obligation Quote</div>
                      <small className="text-body-secondary">Tell us about your project to get matched.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <SectionCard title="Contact & Location">
                                <SectionContactLocation
                                  values={{
                                    fullName, email, phone,
                                    address1, address2, city, state, zipCode,
                                    propertyType, stories, approxSqFt, roomsCount,
                                  }}
                                  setters={{
                                    setFullName, setEmail, setPhone,
                                    setAddress1, setAddress2, setCity, setState, setZipCode,
                                    setPropertyType, setStories, setApproxSqFt, setRoomsCount,
                                  }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 1 && (
                            <Col xs={12}>
                              <SectionCard title="Scope & Surfaces">
                                <SectionScopeSurfaces
                                  values={{
                                    projectType, paintProvided, colorSelected,
                                    surfWalls, surfCeilings, surfTrim, surfDoors, surfCabinets,
                                    surfDeckFence, surfSiding, surfStucco, surfBrick,
                                  }}
                                  setters={{
                                    setProjectType, setPaintProvided, setColorSelected,
                                    setSurfWalls, setSurfCeilings, setSurfTrim, setSurfDoors, setSurfCabinets,
                                    setSurfDeckFence, setSurfSiding, setSurfStucco, setSurfBrick,
                                  }}
                                  V={V}
                                  vp={vp}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 2 && (
                            <>
                              <Col xs={12}>
                                <SectionCard title="Prep & Access">
                                  <SectionPrepAccess
                                    values={{
                                      needsRepairs, wallpaperRemoval, popcornRemoval, pressureWash,
                                      occupancy, accessNotes,
                                    }}
                                    setters={{
                                      setNeedsRepairs, setWallpaperRemoval, setPopcornRemoval, setPressureWash,
                                      setOccupancy, setAccessNotes,
                                    }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                              <Col xs={12}>
                                <SectionCard title="Timeline & Budget">
                                 <SectionTimelineBudget
                                    values={{ preferredStartDate, timingFlexibility, budgetMin, budgetMax }}
                                    setters={{ setPreferredStartDate, setTimingFlexibility, setBudgetMin, setBudgetMax }}
                                    V={V}
                                    vp={vp}             // <-- add this
                                    vpOpt={vpOpt}
                                    />
                                </SectionCard>
                              </Col>
                              <Col xs={12}>
                                <SectionCard title="Notes / Photos (optional)">
                                  <SectionNotesPhotos
                                    values={{ notes, photoUrls }}
                                    setters={{ setNotes, setPhotoUrls }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </>
                          )}

                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // VERTICAL VIEW (reuse the same sections)
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              <Col xs={12}>
                                <SectionCard title="Contact & Location">
                                  <SectionContactLocation
                                    values={{
                                      fullName, email, phone,
                                      address1, address2, city, state, zipCode,
                                      propertyType, stories, approxSqFt, roomsCount,
                                    }}
                                    setters={{
                                      setFullName, setEmail, setPhone,
                                      setAddress1, setAddress2, setCity, setState, setZipCode,
                                      setPropertyType, setStories, setApproxSqFt, setRoomsCount,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Scope & Surfaces">
                                  <SectionScopeSurfaces
                                    values={{
                                      projectType, paintProvided, colorSelected,
                                      surfWalls, surfCeilings, surfTrim, surfDoors, surfCabinets,
                                      surfDeckFence, surfSiding, surfStucco, surfBrick,
                                    }}
                                    setters={{
                                      setProjectType, setPaintProvided, setColorSelected,
                                      setSurfWalls, setSurfCeilings, setSurfTrim, setSurfDoors, setSurfCabinets,
                                      setSurfDeckFence, setSurfSiding, setSurfStucco, setSurfBrick,
                                    }}
                                    V={V}
                                    vp={vp}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Prep & Access">
                                  <SectionPrepAccess
                                    values={{
                                      needsRepairs, wallpaperRemoval, popcornRemoval, pressureWash,
                                      occupancy, accessNotes,
                                    }}
                                    setters={{
                                      setNeedsRepairs, setWallpaperRemoval, setPopcornRemoval, setPressureWash,
                                      setOccupancy, setAccessNotes,
                                    }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Timeline & Budget">
                               <SectionTimelineBudget
                                    values={{ preferredStartDate, timingFlexibility, budgetMin, budgetMax }}
                                    setters={{ setPreferredStartDate, setTimingFlexibility, setBudgetMin, setBudgetMax }}
                                    V={V}
                                    vp={vp}             // <-- add this
                                    vpOpt={vpOpt}
                                    />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Notes / Photos (optional)">
                                  <SectionNotesPhotos
                                    values={{ notes, photoUrls }}
                                    setters={{ setNotes, setPhotoUrls }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </Row>

                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about painting services. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to a great paint job.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your project</h6>
                  <p className="mb-0 text-body-secondary">Location, scope, surfaces, and timing.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">We review & connect you</h6>
                  <p className="mb-0 text-body-secondary">A coordinator reaches out to confirm details.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">Get a detailed estimate</h6>
                  <p className="mb-0 text-body-secondary">Clear pricing and timeline from vetted pros.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Quick answers about painting services.</p>
            </Col>
          </Row>


          <div>FAQ COMPONENT GOES HEARE </div>
          {/* <FAQ_Component_v02b_Simple
            faqs={paintingFAQs.map((f) => ({ question: f.q, answer: f.a }))}
            openFirst
          /> */}
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Easy Painting. All rights reserved.
              <div className="mt-2">
                Services subject to availability. This page is for general information and not a contract.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033375434">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ============================================================
   REUSABLE SECTION COMPONENTS
============================================================ */

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>{title}</strong>
    </legend>
    <div className="mt-1">{children}</div>
  </fieldset>
);

/* ---------- Contact & Location ---------- */
const SectionContactLocation: React.FC<{
  values: {
    fullName: string; email: string; phone: string;
    address1: string; address2: string; city: string; state: string; zipCode: string;
    propertyType: PropertyType; stories: Stories; approxSqFt: string; roomsCount: string;
  };
  setters: {
    setFullName: (v: string) => void; setEmail: (v: string) => void; setPhone: (v: string) => void;
    setAddress1: (v: string) => void; setAddress2: (v: string) => void; setCity: (v: string) => void; setState: (v: string) => void; setZipCode: (v: string) => void;
    setPropertyType: (v: PropertyType) => void; setStories: (v: Stories) => void; setApproxSqFt: (v: string) => void; setRoomsCount: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const {
    fullName, email, phone,
    address1, address2, city, state, zipCode,
    propertyType, stories, approxSqFt, roomsCount,
  } = values;

  const {
    setFullName, setEmail, setPhone,
    setAddress1, setAddress2, setCity, setState, setZipCode,
    setPropertyType, setStories, setApproxSqFt, setRoomsCount,
  } = setters;

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="fullName">Full Name</Form.Label>
        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
        <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
      </Col>

      <Col md={8}>
        <Form.Label htmlFor="address1">Street Address</Form.Label>
        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
        <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="address2">Unit / Apt</Form.Label>
        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Apt 2B" {...vpOpt("address2", !!address2.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="city">City</Form.Label>
        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
        <Form.Control.Feedback type="invalid">Enter your city.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="state">State</Form.Label>
        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
          <option value="">Select</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Form.Select>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="zipCode">Zip</Form.Label>
        <Form.Control id="zipCode" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
        <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="propertyType">Property Type</Form.Label>
        <Form.Select id="propertyType" value={propertyType} onChange={(e) => setPropertyType(e.target.value as PropertyType)} required {...vp("propertyType", V.propertyType)}>
          <option value="">Select</option>
          <option value="single_family">Single-Family</option>
          <option value="apartment">Apartment</option>
          <option value="condo">Condo</option>
          <option value="townhome">Townhome</option>
          <option value="duplex">Duplex</option>
          <option value="commercial">Commercial</option>
          <option value="other">Other</option>
        </Form.Select>
      </Col>

      <Col md={3}>
        <Form.Label htmlFor="stories">Stories</Form.Label>
        <Form.Select id="stories" value={stories} onChange={(e) => setStories(e.target.value as Stories)}>
          <option value="">Select</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3+">3+</option>
        </Form.Select>
      </Col>

      <Col md={3}>
        <Form.Label htmlFor="approxSqFt">Approx Sq Ft</Form.Label>
        <Form.Control id="approxSqFt" inputMode="numeric" value={approxSqFt} onChange={(e) => setApproxSqFt(e.target.value)} placeholder="e.g., 1800" {...vp("approxSqFt", V.approxSqFt)} />
      </Col>

      <Col md={3}>
        <Form.Label htmlFor="roomsCount">Rooms (interior)</Form.Label>
        <Form.Control id="roomsCount" inputMode="numeric" value={roomsCount} onChange={(e) => setRoomsCount(e.target.value)} placeholder="e.g., 6" {...vp("roomsCount", V.roomsCount)} />
      </Col>
    </Row>
  );
};

/* ---------- Scope & Surfaces ---------- */
const SectionScopeSurfaces: React.FC<{
  values: {
    projectType: ProjectType;
    paintProvided: boolean;
    colorSelected: boolean;

    surfWalls: boolean; surfCeilings: boolean; surfTrim: boolean; surfDoors: boolean; surfCabinets: boolean;
    surfDeckFence: boolean; surfSiding: boolean; surfStucco: boolean; surfBrick: boolean;
  };
  setters: {
    setProjectType: (v: ProjectType) => void;
    setPaintProvided: (v: boolean) => void;
    setColorSelected: (v: boolean) => void;

    setSurfWalls: (v: boolean) => void; setSurfCeilings: (v: boolean) => void; setSurfTrim: (v: boolean) => void; setSurfDoors: (v: boolean) => void; setSurfCabinets: (v: boolean) => void;
    setSurfDeckFence: (v: boolean) => void; setSurfSiding: (v: boolean) => void; setSurfStucco: (v: boolean) => void; setSurfBrick: (v: boolean) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
}> = ({ values, setters, V, vp }) => {
  const { projectType, paintProvided, colorSelected, surfWalls, surfCeilings, surfTrim, surfDoors, surfCabinets, surfDeckFence, surfSiding, surfStucco, surfBrick } = values;
  const { setProjectType, setPaintProvided, setColorSelected, setSurfWalls, setSurfCeilings, setSurfTrim, setSurfDoors, setSurfCabinets, setSurfDeckFence, setSurfSiding, setSurfStucco, setSurfBrick } = setters;

  return (
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="projectType">Project Type</Form.Label>
        <Form.Select id="projectType" value={projectType} onChange={(e) => setProjectType(e.target.value as ProjectType)} required {...vp("projectType", V.projectType)}>
          <option value="">Select</option>
          <option value="interior">Interior</option>
          <option value="exterior">Exterior</option>
          <option value="both">Both</option>
        </Form.Select>
      </Col>

      <Col md={6} className="pt-md-4">
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="paintProvided" type="checkbox" label="I will provide paint" checked={paintProvided} onChange={(e) => setPaintProvided(e.currentTarget.checked)} />
          <Form.Check id="colorSelected" type="checkbox" label="Colors already selected" checked={colorSelected} onChange={(e) => setColorSelected(e.currentTarget.checked)} />
        </div>
      </Col>

      <Col xs={12}>
        <div className="fw-semibold mt-2">Surfaces to Paint</div>
        <div className="d-flex flex-wrap gap-3 pt-1">
          <Form.Check id="surfWalls" type="checkbox" label="Walls" checked={surfWalls} onChange={(e) => setSurfWalls(e.currentTarget.checked)} />
          <Form.Check id="surfCeilings" type="checkbox" label="Ceilings" checked={surfCeilings} onChange={(e) => setSurfCeilings(e.currentTarget.checked)} />
          <Form.Check id="surfTrim" type="checkbox" label="Trim" checked={surfTrim} onChange={(e) => setSurfTrim(e.currentTarget.checked)} />
          <Form.Check id="surfDoors" type="checkbox" label="Doors" checked={surfDoors} onChange={(e) => setSurfDoors(e.currentTarget.checked)} />
          <Form.Check id="surfCabinets" type="checkbox" label="Cabinets" checked={surfCabinets} onChange={(e) => setSurfCabinets(e.currentTarget.checked)} />
          <Form.Check id="surfDeckFence" type="checkbox" label="Deck/Fence" checked={surfDeckFence} onChange={(e) => setSurfDeckFence(e.currentTarget.checked)} />
          <Form.Check id="surfSiding" type="checkbox" label="Siding" checked={surfSiding} onChange={(e) => setSurfSiding(e.currentTarget.checked)} />
          <Form.Check id="surfStucco" type="checkbox" label="Stucco" checked={surfStucco} onChange={(e) => setSurfStucco(e.currentTarget.checked)} />
          <Form.Check id="surfBrick" type="checkbox" label="Brick" checked={surfBrick} onChange={(e) => setSurfBrick(e.currentTarget.checked)} />
        </div>
      </Col>
    </Row>
  );
};

/* ---------- Prep & Access ---------- */
const SectionPrepAccess: React.FC<{
  values: {
    needsRepairs: boolean; wallpaperRemoval: boolean; popcornRemoval: boolean; pressureWash: boolean;
    occupancy: Occupancy; accessNotes: string;
  };
  setters: {
    setNeedsRepairs: (v: boolean) => void; setWallpaperRemoval: (v: boolean) => void; setPopcornRemoval: (v: boolean) => void; setPressureWash: (v: boolean) => void;
    setOccupancy: (v: Occupancy) => void; setAccessNotes: (v: string) => void;
  };
  V: any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vpOpt }) => {
  const { needsRepairs, wallpaperRemoval, popcornRemoval, pressureWash, occupancy, accessNotes } = values;
  const { setNeedsRepairs, setWallpaperRemoval, setPopcornRemoval, setPressureWash, setOccupancy, setAccessNotes } = setters;

  return (
    <Row className="g-3">
      <Col md={12}>
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="needsRepairs" type="checkbox" label="Minor repairs / patching" checked={needsRepairs} onChange={(e) => setNeedsRepairs(e.currentTarget.checked)} />
          <Form.Check id="wallpaperRemoval" type="checkbox" label="Wallpaper removal" checked={wallpaperRemoval} onChange={(e) => setWallpaperRemoval(e.currentTarget.checked)} />
          <Form.Check id="popcornRemoval" type="checkbox" label="Popcorn removal" checked={popcornRemoval} onChange={(e) => setPopcornRemoval(e.currentTarget.checked)} />
          <Form.Check id="pressureWash" type="checkbox" label="Pressure wash (exterior)" checked={pressureWash} onChange={(e) => setPressureWash(e.currentTarget.checked)} />
        </div>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="occupancy">Occupancy</Form.Label>
        <Form.Select id="occupancy" value={occupancy} onChange={(e) => setOccupancy(e.target.value as Occupancy)}>
          <option value="">Select</option>
          <option value="occupied">Occupied</option>
          <option value="vacant">Vacant</option>
          <option value="between_tenants">Between tenants</option>
        </Form.Select>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="accessNotes">Access Notes (optional)</Form.Label>
        <Form.Control id="accessNotes" value={accessNotes} onChange={(e) => setAccessNotes(e.target.value)} placeholder="Gate codes, parking, pets, etc." {...vpOpt("accessNotes", !!accessNotes.trim())} />
      </Col>
    </Row>
  );
};

/* ---------- Timeline & Budget ---------- */
// Replace the current signature for SectionTimelineBudget with this:
const SectionTimelineBudget: React.FC<{
  values: { preferredStartDate: string; timingFlexibility: Flex; budgetMin: string; budgetMax: string };
  setters: {
    setPreferredStartDate: (v: string) => void;
    setTimingFlexibility: (v: Flex) => void;
    setBudgetMin: (v: string) => void;
    setBudgetMax: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;              // <-- add this
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {             // <-- include vp in destructuring
  const { preferredStartDate, timingFlexibility, budgetMin, budgetMax } = values;
  const { setPreferredStartDate, setTimingFlexibility, setBudgetMin, setBudgetMax } = setters;

  return (
    <Row className="g-3">
      <Col md={4}>
        <Form.Label htmlFor="startDate">Preferred Start Date</Form.Label>
        <Form.Control
          id="startDate"
          type="date"
          value={preferredStartDate}
          onChange={(e) => setPreferredStartDate(e.target.value)}
          {...vpOpt("preferredStartDate", !!preferredStartDate)}
        />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="flex">Timing</Form.Label>
        <Form.Select
          id="flex"
          value={timingFlexibility}
          onChange={(e) => setTimingFlexibility(e.target.value as Flex)}
        >
          <option value="">Select</option>
          <option value="asap">ASAP</option>
          <option value="within_2_weeks">Within 2 weeks</option>
          <option value="flexible">Flexible</option>
        </Form.Select>
      </Col>

      <Col md={2}>
        <Form.Label htmlFor="budgetMin">Budget Min</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            id="budgetMin"
            inputMode="numeric"
            value={budgetMin}
            onChange={(e) => setBudgetMin(e.target.value)}
            placeholder="e.g., 1500"
            {...vp("budgetMin", V.budgetMin)}            // now valid
          />
        </InputGroup>
      </Col>

      <Col md={2}>
        <Form.Label htmlFor="budgetMax">Budget Max</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            id="budgetMax"
            inputMode="numeric"
            value={budgetMax}
            onChange={(e) => setBudgetMax(e.target.value)}
            placeholder="e.g., 5000"
            {...vp("budgetMax", V.budgetMax)}            // now valid
          />
        </InputGroup>
      </Col>
    </Row>
  );
};



/* ---------- Notes / Photos ---------- */
const SectionNotesPhotos: React.FC<{
  values: { notes: string; photoUrls: string };
  setters: { setNotes: (v: string) => void; setPhotoUrls: (v: string) => void };
  V: any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vpOpt }) => {
  const { notes, photoUrls } = values;
  const { setNotes, setPhotoUrls } = setters;

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="notes">Additional Notes (optional)</Form.Label>
        <Form.Control id="notes" as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything helpful—paint brand, finish, scheduling constraints..." {...vpOpt("notes", !!notes.trim())} />
      </Col>
      <Col md={12}>
        <Form.Label htmlFor="photoUrls">Photo URLs (optional)</Form.Label>
        <Form.Control id="photoUrls" as="textarea" rows={2} value={photoUrls} onChange={(e) => setPhotoUrls(e.target.value)} placeholder="One URL per line (Drive, Dropbox, etc.)" {...vpOpt("photoUrls", !!photoUrls.trim())} />
        <div className="form-text">Tip: share public links so estimators can view them.</div>
      </Col>
    </Row>
  );
};

/* ============================================================
   Confirmation block (Sectioned, readable, no JSON)
============================================================ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  const safetyPrep: string[] = [];
  if (app.needsRepairs) safetyPrep.push("Repairs/Patching");
  if (app.wallpaperRemoval) safetyPrep.push("Wallpaper Removal");
  if (app.popcornRemoval) safetyPrep.push("Popcorn Removal");
  if (app.pressureWash) safetyPrep.push("Pressure Wash");

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thanks! A project advisor will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        <div className="mt-3">
          <Section title="Contact">
            <KV label="Name"  value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
          </Section>

          <Section title="Location">
            <KV label="Street" value={dash(app.address1)} />
            <KV label="Unit / Apt" value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
          </Section>

          <Section title="Property">
            <KV label="Type" value={app.propertyType ? app.propertyType.replace("_", " ") : "—"} />
            <KV label="Stories" value={dash(app.stories)} />
            <KV label="Approx Sq Ft" value={app.approxSqFt ? money(app.approxSqFt) : "—"} />
            <KV label="Rooms (interior)" value={dash(app.roomsCount)} />
          </Section>

          <Section title="Scope">
            <KV label="Project Type" value={app.projectType ? app.projectType.toUpperCase() : "—"} />
            <KV label="Paint Provided" value={app.paintProvided ? "Yes" : "No"} />
            <KV label="Colors Selected" value={app.colorSelected ? "Yes" : "No"} />
            <KV label="Surfaces" value={app.surfaces && app.surfaces.length ? app.surfaces.join(", ") : "—"} />
          </Section>

          <Section title="Prep & Access">
            <KV label="Prep Items" value={safetyPrep.length ? safetyPrep.join(", ") : "—"} />
            <KV label="Occupancy" value={dash(app.occupancy)} />
            <KV label="Access Notes" value={dash(app.accessNotes)} />
          </Section>

          <Section title="Timeline & Budget">
            <KV label="Preferred Start" value={dash(app.preferredStartDate)} />
            <KV label="Timing" value={dash(app.timingFlexibility)} />
            <KV label="Budget" value={`${app.budgetMin ? money(app.budgetMin) : "—"} – ${app.budgetMax ? money(app.budgetMax) : "—"}`} />
          </Section>

          <Section title="Notes / Photos">
            <div className="p-3 bg-body-tertiary border rounded mb-2">{dash(app.notes)}</div>
            <KV label="Photo URLs" value={dash(app.photoUrls)} />
          </Section>
        </div>

        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033375434" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</Button>
          <Button variant="outline-secondary" onClick={() => window.print()}>Print</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for confirmation layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">{children}</Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default EASY_PAINTING_LandingPage_v02O_WIZARD;
